import { useState, useEffect, useContext, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  Divider, 
  CircularProgress, 
  Alert,
  IconButton,
  Tooltip,
  Fade
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import axios from 'axios';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ScrollContext } from '../Context';
import { ThemeContext } from '../Context';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import GetUserAvatar from '../tool/GetUserAvatar';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Composant pour les rubriques triables
function SortableRubric({ rubric, getBorderColor, setTargetRubric, user, isDark, report }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rubric.id,
    disabled: rubric.isEditing
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    mb: 0.5, 
    p: 0.3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderLeft: getBorderColor(rubric) !== 'transparent' ? `4px solid ${getBorderColor(rubric)}` : 'none',
    background: getBorderColor(rubric) !== 'transparent' 
      ? `linear-gradient(to right, rgba(${getBorderColor(rubric) === '#e67e22' ? '230,126,34' : getBorderColor(rubric) === 'blue' ? '0,0,255' : '255,0,0'},0.03), ${isDark ? '#22303e' : '#ffffff'})`
      : isDark ? '#22303e' : '#ffffff',
    zIndex: isDragging ? 1 : 'auto',
    transition: 'all 0.3s ease',
  };

  return (
    <Paper 
      ref={setNodeRef}
      elevation={1} 
      sx={style}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
          <div {...attributes} {...listeners} style={{ cursor: rubric.isEditing ? 'not-allowed' : 'grab', display: 'flex', alignItems: 'center' }}>
            <DragIndicatorIcon sx={{ mr: 1, color: isDark ? '#95a5a6' : 'text.secondary' }} />
          </div>
          <span style={{ color: isDark ? '#ecf0f1' : 'inherit' }}>{rubric.title}</span>
        </Typography>
        
        <Tooltip title="Voir cette rubrique">
          <IconButton 
            size="small" 
            onClick={() => {
              // Log de la consultation de rubrique
              logAction(LOG_ACTIONS.RUBRIC_VIEW, formatReportNumber(report?.number, report?.year), {
                rubricId: rubric.id,
                rubricTitle: rubric.title
              });
              setTargetRubric(rubric.id);
            }}
            aria-label="Voir cette rubrique"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      {rubric.isEditing && (
        <Typography variant="caption" sx={{ 
          display: 'flex',
          alignItems: 'center',
          color: isDark ? '#95a5a6' : 'text.secondary',
          fontStyle: 'italic',
          ml: 4
        }}>
          <span>En cours d'édition par</span>
          <Box component="span" sx={{ display: 'inline-flex', ml: 0.5 }}>
            {rubric.editedBy === (typeof user === 'object' ? user?.matricule : user) ? (
              "moi"
            ) : (
              <GetUserAvatar matricule={rubric.editedBy} defaultValue="..." />
            )}
          </Box>
        </Typography>
      )}
    </Paper>
  );
}

function Summary() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { setTargetRubric } = useContext(ScrollContext);
  const { theme } = useContext(ThemeContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Configuration des capteurs pour dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Tri des rubriques par position (fait avant les conditions de retour)
  const sortedRubrics = useMemo(() => {
    if (!reportData || !reportData.rubrics) return [];
    return [...reportData.rubrics].sort((a, b) => a.position - b.position);
  }, [reportData]);

  // Liste des IDs pour le contexte de tri (déplacé avant les conditions de retour)
  const itemIds = useMemo(() => 
    sortedRubrics.map(rubric => rubric.id), [sortedRubrics]
  );
  
  // Fonction pour déterminer si une rubrique est de type GuardIA
  const isGuardiaRubric = (rubric) => {
    return rubric.title === 'Rubrique GuardIA' || rubric.isGuardia;
  };

  // Fonction pour déterminer la couleur de bordure selon les critères
  const getBorderColor = (rubric) => {
    const currentUserMatricule = typeof user === 'object' ? user?.matricule : user;

    // Si la rubrique est en cours d'édition
    if (rubric.isEditing) {
      // Vérifier si c'est l'utilisateur actuel qui édite
      return rubric.editedBy === currentUserMatricule ? '#e67e22' : 'blue';
    }
    // Si la rubrique n'est pas générée et n'est pas en cours d'édition
    else if (!rubric.generated) {
      // Pour les rubriques GuardIA non générées, utiliser la couleur mauve
      if (isGuardiaRubric(rubric)) {
        return isDark ? '#c084fc' : '#800080';
      } else {
        return 'red';
      }
    }

    // Par défaut (generated = true, isEditing = false)
    return 'transparent';
  };

  // Charger les données du rapport
  useEffect(() => {
    if (!report) return;

    const fetchReportData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setReportData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données du rapport:', error);
        setError('Impossible de charger les rubriques du rapport. Veuillez réessayer.');
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Écouter les mises à jour du rapport via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdate = (updatedReport) => {
      console.log('Mise à jour de rapport reçue:', updatedReport);
      
      if (updatedReport.number === report.number && updatedReport.year === report.year) {
        // Mise à jour des rubriques
        if (updatedReport.rubrics) {
          console.log('Mise à jour des rubriques reçue:', updatedReport.rubrics);
          setReportData(prevData => {
            if (!prevData) return null;
            return {
              ...prevData,
              rubrics: updatedReport.rubrics
            };
          });
        }
      }
    };
    
    // Écouter également les événements émis directement par le client
    const handleDirectUpdate = (data) => {
      console.log('Mise à jour directe reçue:', data);
      
      if (data.reportId === `${report.number}-${report.year}` && data.rubrics) {
        console.log('Mise à jour directe des rubriques:', data.rubrics);
        setReportData(prevData => {
          if (!prevData) return null;
          return {
            ...prevData,
            rubrics: data.rubrics
          };
        });
      }
    };

    socket.on('reportUpdated', handleReportUpdate);
    socket.on('updateReport', handleDirectUpdate);

    // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***

    return () => {
      socket.off('reportUpdated', handleReportUpdate);
      socket.off('updateReport', handleDirectUpdate);
      // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***
    };
  }, [socket, report]);

  // Gestionnaire pour la fin d'un glisser-déposer
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // Mise à jour locale
    setReportData(prevData => {
      if (!prevData || !prevData.rubrics) return prevData;
      
      const rubrics = [...prevData.rubrics];
      const oldIndex = rubrics.findIndex(rubric => rubric.id === active.id);
      const newIndex = rubrics.findIndex(rubric => rubric.id === over.id);
      
      // Réorganiser les positions
      const reorderedRubrics = arrayMove(rubrics, oldIndex, newIndex);
      reorderedRubrics.forEach((rubric, index) => {
        rubric.position = index + 1;
      });
      
      return {
        ...prevData,
        rubrics: reorderedRubrics
      };
    });
    
    // Mise à jour via API
    try {
      if (!reportData) return;
      
      const updatedRubrics = [...reportData.rubrics];
      const oldIndex = updatedRubrics.findIndex(rubric => rubric.id === active.id);
      const newIndex = updatedRubrics.findIndex(rubric => rubric.id === over.id);
      
      const reorderedRubrics = arrayMove(updatedRubrics, oldIndex, newIndex);
      reorderedRubrics.forEach((rubric, index) => {
        rubric.position = index + 1;
      });
      
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData._id;
      
      await axios.put(
        `/api/report/${reportId}/rubrics`,
        { rubrics: reorderedRubrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: reorderedRubrics
        });
        
        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: reorderedRubrics
        });
      }

      // Log du déplacement de rubrique
      const movedRubric = reorderedRubrics.find(r => r.id === active.id);
      logAction(LOG_ACTIONS.RUBRIC_MOVE, formatReportNumber(report.number, report.year), {
        rubricId: active.id,
        rubricTitle: movedRubric?.title || null,
        fromPosition: oldIndex + 1,
        toPosition: newIndex + 1
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des positions:', error);
    }
  };

  // Afficher un indicateur de chargement
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        p: 3,
        backgroundColor: isDark ? '#1a252f' : 'inherit'
      }}>
        <CircularProgress sx={{
          color: isDark ? '#4fc3f7' : undefined
        }} />
      </Box>
    );
  }

  // Afficher un message d'erreur
  if (error) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? '#1a252f' : 'inherit'
      }}>
        <Alert 
          severity="error"
          sx={{
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ff6b6b' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ff6b6b' : undefined
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Afficher un message si aucun rapport n'est sélectionné
  if (!report) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? '#1a252f' : 'inherit'
      }}>
        <Alert 
          severity="info"
          sx={{
            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : undefined,
            color: isDark ? '#4fc3f7' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#4fc3f7' : undefined
            }
          }}
        >
          Veuillez sélectionner un rapport pour afficher son contenu.
        </Alert>
      </Box>
    );
  }

  // Afficher un message élégant si aucune rubrique n'est disponible
  if (!reportData || !reportData.rubrics || reportData.rubrics.length === 0) {
    return (
      <Fade in={true} timeout={800}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: isDark ? '#1a252f' : 'inherit'
        }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(25, 118, 210, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: isDark 
                  ? '0 0 0 0 rgba(79, 195, 247, 0.4)' 
                  : '0 0 0 0 rgba(25, 118, 210, 0.3)',
              },
              '50%': {
                transform: 'scale(1.05)',
                boxShadow: isDark 
                  ? '0 0 20px 10px rgba(79, 195, 247, 0)' 
                  : '0 0 20px 10px rgba(25, 118, 210, 0)',
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: isDark 
                  ? '0 0 0 0 rgba(79, 195, 247, 0)' 
                  : '0 0 0 0 rgba(25, 118, 210, 0)',
              },
            },
          }}>
            <DragIndicatorIcon sx={{ 
              fontSize: 36, 
              color: isDark ? '#4fc3f7' : 'primary.main',
              opacity: 0.8
            }} />
          </Box>
          <Typography 
            variant="body1" 
            sx={{ 
              color: isDark ? '#bdc3c7' : 'text.secondary',
              fontWeight: 500,
              mb: 0.5
            }}
          >
            Aucune rubrique
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: isDark ? '#7f8c8d' : 'text.disabled',
              fontStyle: 'italic',
              textAlign: 'center',
              px: 2
            }}
          >
            Vos rubriques apparaîtront ici
          </Typography>
        </Box>
      </Fade>
    );
  }

  // Rendu du composant avec les rubriques
  return (
    <Box sx={{ 
      p: 2, 
      height: '100%', 
      overflow: 'auto',
      backgroundColor: isDark ? '#1a252f' : 'inherit',
      // Styles personnalisés pour les scrollbars en mode sombre
      '&::-webkit-scrollbar': {
        width: isDark ? '8px' : undefined,
        height: isDark ? '8px' : undefined,
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: isDark ? '#22303e' : undefined,
        borderRadius: isDark ? '4px' : undefined,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: isDark ? '#2d3f4c' : undefined,
        borderRadius: isDark ? '4px' : undefined,
        '&:hover': {
          backgroundColor: isDark ? '#384550' : undefined,
        },
      },
      // Pour Firefox
      scrollbarWidth: isDark ? 'thin' : undefined,
      scrollbarColor: isDark ? '#2d3f4c #22303e' : undefined,
    }}>
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={itemIds} 
          strategy={verticalListSortingStrategy}
        >
          {sortedRubrics.map((rubric) => (
            <SortableRubric 
              key={rubric.id}
              rubric={rubric}
              getBorderColor={getBorderColor}
              setTargetRubric={setTargetRubric}
              user={user}
              isDark={isDark}
              report={report}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}

export default Summary;



